import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Target, Gift, Coins, Check, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Golden reward toast — inline styled, no CSS file needed
function goldenToast(coins, title) {
  toast.custom((id) => (
    <div
      data-testid="mission-golden-toast"
      className="relative overflow-hidden rounded-2xl shadow-2xl px-5 py-4 min-w-[280px] max-w-[360px] text-white flex items-center gap-3"
      style={{
        background: "linear-gradient(135deg,#f6d365 0%,#fbc02d 45%,#ff9800 100%)",
        boxShadow: "0 10px 40px -10px rgba(251,192,45,.65), 0 0 0 1px rgba(255,255,255,.35) inset",
      }}
    >
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/25 blur-2xl pointer-events-none" />
      <div className="w-11 h-11 rounded-full bg-white/25 backdrop-blur-md flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-6 h-6 drop-shadow" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Missão concluída</div>
        <div className="text-sm font-bold leading-tight truncate">{title || "Recompensa desbloqueada"}</div>
        <div className="flex items-center gap-1 text-sm font-black">
          <Coins className="w-4 h-4" />+{coins} moedas FUTURO PDV
        </div>
      </div>
      <button
        onClick={() => toast.dismiss(id)}
        className="text-white/70 hover:text-white text-xs font-bold"
        aria-label="Fechar"
      >
        ×
      </button>
    </div>
  ), { duration: 4200 });
}

function fireConfetti() {
  const opts = {
    spread: 70,
    startVelocity: 45,
    ticks: 200,
    scalar: 1.05,
    colors: ["#fbc02d", "#ff9800", "#f6d365", "#ffffff", "#fde68a"],
    origin: { y: 0.3 },
  };
  confetti({ ...opts, particleCount: 90, angle: 60, origin: { x: 0, y: 0.4 } });
  confetti({ ...opts, particleCount: 90, angle: 120, origin: { x: 1, y: 0.4 } });
  confetti({ ...opts, particleCount: 120, angle: 90, origin: { x: 0.5, y: 0.3 } });
}

const DailyMissions = forwardRef(function DailyMissions(_, ref) {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [pulseId, setPulseId] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/missions/today`);
      setMissions(r.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({ refresh: load }));

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const claim = async (m) => {
    setClaiming(m.mission_id);
    try {
      const r = await axios.post(`${API}/missions/${m.mission_id}/claim`);
      fireConfetti();
      goldenToast(r.data.coins, m.title);
      setPulseId(m.mission_id);
      setTimeout(() => setPulseId((cur) => (cur === m.mission_id ? null : cur)), 1200);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setClaiming(null);
  };

  if (loading || !missions.length) return null;

  return (
    <Card className="p-5 space-y-4" data-testid="daily-missions">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Target className="w-5 h-5 text-primary" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Missões de hoje</div>
          <div className="text-sm font-semibold">Complete e ganhe moedas FUTURO PDV</div>
        </div>
      </div>
      <div className="space-y-2">
        {missions.map((m) => {
          const pct = Math.min(100, Math.round((m.progress / (m.target || 1)) * 100));
          const done = !!m.completed_at;
          const claimed = !!m.claimed_at;
          return (
            <div
              key={m.mission_id}
              className={`p-3 rounded-xl border transition-colors ${pulseId === m.mission_id ? "border-yellow-400 bg-yellow-400/10 animate-pulse" : "border-border"}`}
              data-testid={`mission-${m.mission_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="text-2xl leading-none">{m.icon || "🎯"}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{m.title}</div>
                  {m.description && <div className="text-[11px] text-muted-foreground line-clamp-1">{m.description}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    <Progress value={pct} className="h-1.5 flex-1" data-testid={`mission-progress-${m.mission_id}`} />
                    <span className="text-[11px] text-muted-foreground font-mono">{m.progress}/{m.target}</span>
                  </div>
                </div>
                {claimed ? (
                  <div className="text-[11px] flex items-center gap-1 text-emerald-600 font-semibold" data-testid={`mission-claimed-${m.mission_id}`}>
                    <Check className="w-3 h-3" /> Resgatado
                  </div>
                ) : done ? (
                  <Button
                    size="sm"
                    onClick={() => claim(m)}
                    disabled={claiming === m.mission_id}
                    className="rounded-full gap-1 h-8 text-xs"
                    data-testid={`mission-claim-${m.mission_id}`}
                  >
                    <Gift className="w-3.5 h-3.5" /> +{m.coin_reward}
                  </Button>
                ) : (
                  <div className="text-[11px] font-mono text-primary flex items-center gap-1">
                    <Coins className="w-3 h-3" /> +{m.coin_reward}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
});

export default DailyMissions;
